<template>
  <div
    id="parent"
    @mouseenter="onItemMouseEnter"
    @mouseleave="onItemMouseLeave"
    @click="onClick"
    class="cursor-pointer relative max-h-48"
  >
    <div
      v-if="!showItemOptions"
      class="absolute hidden-child right-2 top-2 z-50 rounded-lg hover:bg-payneMiddle bg-white w-7 h-7 justify-center items-center border-payne transition-all duration-300 ease-in-out"
      @click="showItemOptions = !showItemOptions"
    >
      <DotsHorizontal class="text-yank hover:text-yankMiddle opacity-50 w-4" />
    </div>
    <div
      v-if="showItemOptions"
      class="absolute flex right-2 top-2 z-50 rounded-lg hover:bg-payneMiddle bg-white w-7 h-7 justify-center items-center border-payne transition-all duration-300 ease-in-out"
      @click="showItemOptions = !showItemOptions"
    >
      <DotsHorizontal class="text-yank hover:text-yankMiddle opacity-50 w-4" />
    </div>
    <div v-if="includesPdf" class="h-48 p-12 rounded-xl">
      <img
        src="images/pdf.svg"
        :alt="item.name"
        loading="lazy"
        class="w-full h-full object-cover"
      />
    </div>
    <img
      v-else-if="!includesVideo"
      :onerror="`this.onerror=null; this.src='${IMAGE_PLACEHOLDER}'`"
      :src="item.thumbnail_url"
      :alt="item.name"
      loading="lazy"
      class="w-full h-48 object-cover rounded-xl"
    />
    <video
      ref="video"
      v-else
      class="w-full h-full object-cover rounded-item"
      muted
      loop
    >
      <source
        :src="item.thumbnail_url"
        :type="
          item.mime_type && item.mime_type !== 'video/quicktime'
            ? item.mime_type
            : 'video/mp4'
        "
      />
      Your browser does not support the video tag.
    </video>
    <div
      v-if="showItemOptions"
      v-on-clickaway="away"
      class="absolute w-48 h-30 top-10 right-2 bg-white z-50 rounded-lg shadow-xl p-2"
    >
      <div @click="download">
        <a
          dusk="download-button"
          class="p-2 flex flex-row items-center justify-start hover:bg-smoke rounded-t-xl"
          :href="
            $route('download', { type: 'media', uuid: item.files[0].uuid })
          "
        >
          <DownloadIcon class="mr-2 w-3 h-3 text-yank opacity-40" />
          <p class="text-xs text-yank">Download</p>
        </a>
      </div>
      <div
        @click="$emit('click')"
        class="p-2 flex flex-row items-center justify-start hover:bg-smoke rounded-t-xl"
      >
        <EditAltIcon class="mr-2 w-3 h-3 text-yank opacity-40" />
        <p class="text-xs text-yank">Edit</p>
      </div>
      <div
        @click.stop="enterMoveModal"
        class="p-2 flex flex-row items-center justify-start hover:bg-smoke rounded-t-xl"
      >
        <MoveFolderIcon class="mr-2 w-3 h-3 text-yank opacity-40" />
        <p class="text-xs text-yank">Move</p>
      </div>
      <div
        @click="$emit('rename')"
        class="p-2 flex flex-row items-center justify-start hover:bg-smoke rounded-t-xl"
      >
        <EditIcon class="mr-2 w-3 h-3 text-yank opacity-40" />
        <p class="text-xs text-yank">Rename</p>
      </div>
      <div
        class="p-2 flex flex-row items-center justify-start hover:bg-smoke rounded-b-xl"
        @click.stop="deleteItem"
      >
        <TrashIcon class="mr-2 w-3 h-3 text-yank opacity-40" />
        <p class="text-xs text-yank">Delete</p>
      </div>
    </div>
    <div
      v-show="showHoverContent"
      class="h-full w-full rounded-item absolute top-0 bg-gradient-to-t from-gray-900 opacity-50 z-30"
    ></div>
    <div
      v-show="showHoverContent"
      class="h-full w-full flex flex-col-reverse absolute top-0 p-2 z-40"
    >
      <div class="absolute bottom-0 right-3">
        <div v-if="withFavorite" class="relative z-20" @click.stop>
          <button
            v-if="!item.favorited"
            @click.stop.prevent="toggleFavorite"
            type="button"
            class="text-white text-opacity-50 hover:text-opacity-100 transition outline-none focus:outline-none"
          >
            <svg
              v-if="!processingFavorite"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <svg
              v-if="processingFavorite"
              class="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </button>

          <button
            v-if="item.favorited"
            @click.stop.prevent="toggleFavorite"
            type="button"
            class="text-white hover:text-opacity-90 transition outline-none focus:outline-none"
          >
            <svg
              v-if="!processingFavorite"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-if="processingFavorite"
              class="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <portal to="destination">
      <div
        v-if="showMoveModal"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <MoveItemModal
          v-on="$listeners"
          @closeModal="showMoveModal = false"
          :item="item"
        />
      </div>
    </portal>
  </div>
</template>
<script>
import axios from "axios";
import { mixin as clickaway } from "vue-clickaway";
import DotsHorizontal from "@/components/svg/DotsHorizontal";
import EditIcon from "@/components/svg/EditIcon";
import EditAltIcon from "@/components/svg/EditAltIcon";
import TrashIcon from "@/components/svg/TrashIcon";
import DownloadIcon from "@/components/svg/Download";
import MoveFolderIcon from "@/components/svg/MoveFolderIcon";
import { IMAGE_PLACEHOLDER } from "@/utils/constants";
import MoveItemModal from "@/components/my-media/MoveItemModal.vue";

export default {
  name: "ChurchItemComponent",
  mixins: [clickaway],
  data() {
    return {
      showItemOptions: false,
      showHoverContent: false,
      processingFavorite: false,
      IMAGE_PLACEHOLDER,
      showMoveModal: false,
    };
  },
  components: {
    DotsHorizontal,
    EditIcon,
    EditAltIcon,
    TrashIcon,
    DownloadIcon,
    MoveFolderIcon,
    MoveItemModal,
  },
  props: {
    item: Object,
    withFavorite: {
      type: Boolean,
      default: false,
    },
    flash: Object,
  },
  computed: {
    includesVideo() {
      return (
        this.item &&
        this.item.mime_type &&
        this.item.mime_type.includes("video")
      );
    },

    includesPdf() {
      return (
        this.item &&
        this.item.mime_type &&
        this.item.mime_type.includes("pdf")
      );
    }
  },
  methods: {
    away() {
      this.showItemOptions = false;
    },
    deleteItem() {
      this.$emit("deleteItem", this.item);
    },

    download() {
      this.$toast.success("The download will start shortly.");
    },

    enterMoveModal() {
      this.showMoveModal = true;
      this.showItemOptions = false;
      if (this.includesVideo) {
        this.$refs.video.pause();
      }
    },

    onItemMouseEnter() {
      this.showHoverContent = true;
      if (this.includesVideo) {
        this.$refs.video.play();
      }
    },

    onItemMouseLeave() {
      this.showHoverContent = false;
      if (this.includesVideo) {
        this.$refs.video.pause();
      }
    },
    onClick() {
      if (!this.showItemOptions) {
        this.$emit("click");
      }
    },
    toggleFavorite() {
      if (!this.$page.props.user) {
        this.$inertia.visit(this.$route("login.show"));
        return;
      }

      (this.processingFavorite = true),
        axios
          .patch(this.$route("church-media.favorites.toggle", this.item.id), {
            redirect: false,
          })
          .then((response) => {
            this.item.favorited = response.data.favorited;
          })
          .finally(() => (this.processingFavorite = false));
    },
  },

  mounted() {
    console.log("ITEM", this.item);
  }
};
</script>
<style scoped>
#parent .hidden-child {
  display: none;
}
#parent:hover .hidden-child {
  display: flex;
}
</style>
